import { Checkbox, Switch } from "@octopusdeploy/design-system-components";
import type { ParentEnvironmentResource } from "@octopusdeploy/octopus-server-client";
import * as React from "react";
import ComponentRow from "~/components/ComponentRow";
import Markdown from "~/components/Markdown";
import { ExpandableFormSection, MarkdownEditor, required, Summary, Text } from "~/components/form";
import NumberPicker from "~/primitiveComponents/form/Number/NumberPicker";
import NameSummaryWithSlug from "~/primitiveComponents/form/Slugs/NameSummaryWithSlug";
import SlugEditor from "~/primitiveComponents/form/Slugs/SlugEditor";
import { getAutomaticDeprovisioningRuleDescription } from "./getAutomaticDeprovisioningRuleDescription";
interface ParentEnvironmentFormProps {
    model: ParentEnvironmentResource;
    setModel: (model: ParentEnvironmentResource) => void;
    getFieldError: (fieldName: string) => string | undefined;
}
export function ParentEnvironmentForm({ model, setModel, getFieldError }: ParentEnvironmentFormProps) {
    const descriptionSummary = () => {
        return model.Description ? Summary.summary(<Markdown markup={model.Description}/>) : Summary.placeholder("No description provided");
    };
    const ShouldExpire = (model: ParentEnvironmentResource) => {
        return model.AutomaticDeprovisioningRule.ExpiryDays > 0 || model.AutomaticDeprovisioningRule.ExpiryHours > 0;
    };
    return (<>
            <ExpandableFormSection errorKey="name" title="Name" focusOnExpandAll summary={model.Name ? Summary.summary(<NameSummaryWithSlug name={model.Name} slug={model.Slug}/>) : Summary.placeholder("Please enter a name for your environment")} help="A short, memorable, unique name for this parent environment. Example: QA Testing.">
                <Text value={model.Name} onChange={(Name) => setModel({ ...model, Name })} label="Name" validate={required("Please enter a environment name")} autoFocus={true}/>
                <SlugEditor value={model.Slug} name={model.Name} originalSlug={model.Slug ?? ""} onChange={(Slug) => setModel({ ...model, Slug })} label="Slug" validate={required("Please enter an environment slug")} error={getFieldError("Slug")}/>
            </ExpandableFormSection>

            <ExpandableFormSection errorKey="description" title="Description" summary={descriptionSummary()} help="Enter a description for your environment.">
                <MarkdownEditor value={model.Description} label="Environment description" onChange={(Description) => setModel({ ...model, Description })}/>
            </ExpandableFormSection>

            <ExpandableFormSection errorKey="UseGuidedFailure" title="Default Guided Failure Mode" summary={model.UseGuidedFailure ? Summary.summary("Yes") : Summary.default("No")} help="Select whether guided failure mode is enabled by default">
                <div>
                    <Checkbox value={model.UseGuidedFailure ?? false} label="Use guided failure mode by default" onChange={(UseGuidedFailure: boolean) => setModel({ ...model, UseGuidedFailure })} note={<span>If guided failure is enabled for an environment, Octopus Deploy will prompt for user intervention if a deployment fails in the environment.</span>}/>
                </div>
            </ExpandableFormSection>

            <ExpandableFormSection errorKey="AutomaticDeprovisioningRule" title="Automatic Deprovisioning" help="Automatically deprovision environments if there's no activity for the selected expiry period" summary={ShouldExpire(model) ? Summary.summary(getAutomaticDeprovisioningRuleDescription(model.AutomaticDeprovisioningRule)) : Summary.summary("No Expiry")}>
                <div>
                    <Switch value={ShouldExpire(model)} label="Deprovision environment after period of inactivity" onChange={(Expiry: boolean) => {
            if (Expiry) {
                setModel({
                    ...model,
                    AutomaticDeprovisioningRule: {
                        ...model.AutomaticDeprovisioningRule,
                        ExpiryDays: 7, // Default to 7 days if the switch is toggled on
                        ExpiryHours: 0,
                    },
                });
            }
            else {
                setModel({
                    ...model,
                    AutomaticDeprovisioningRule: {
                        ...model.AutomaticDeprovisioningRule,
                        ExpiryDays: 0, // 0 indicates no expiry
                        ExpiryHours: 0,
                    },
                });
            }
        }}/>
                    {ShouldExpire(model) && (<div>
                            Time since last activity
                            <ComponentRow>
                                <div>
                                    <NumberPicker value={model.AutomaticDeprovisioningRule.ExpiryDays} label="Day(s)" onChange={(ExpiryDays) => {
                const safeValue = Math.max(0, ExpiryDays ?? 0);
                setModel({
                    ...model,
                    AutomaticDeprovisioningRule: {
                        ...model.AutomaticDeprovisioningRule,
                        ExpiryDays: safeValue,
                    },
                });
            }}/>
                                </div>
                                <div>
                                    <NumberPicker value={model.AutomaticDeprovisioningRule.ExpiryHours} label="Hour(s)" onChange={(ExpiryHours) => {
                const safeValue = Math.max(0, Math.min(23, ExpiryHours ?? 0));
                setModel({
                    ...model,
                    AutomaticDeprovisioningRule: {
                        ...model.AutomaticDeprovisioningRule,
                        ExpiryHours: safeValue,
                    },
                });
            }}/>
                                </div>
                            </ComponentRow>
                        </div>)}
                </div>
            </ExpandableFormSection>
        </>);
}
